import { useState } from 'react';

export default function TestEmailPage() {
  const [connectionResult, setConnectionResult] = useState<any>(null);
  const [emailResult, setEmailResult] = useState<any>(null);
  const [email, setEmail] = useState<string>('');
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const testConnection = async () => {
    setIsTestingConnection(true);
    setConnectionResult(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-email-nodemailer-working`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ action: 'test' }),
        }
      );

      const result = await response.json();
      setConnectionResult(result);
    } catch (error: any) {
      setConnectionResult({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const sendTestEmail = async () => {
    if (!email) {
      setEmailResult({
        success: false,
        error: 'Please enter an email address',
      });
      return;
    }

    setIsSendingEmail(true);
    setEmailResult(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-email-nodemailer-working`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ action: 'send', testEmail: email }),
        }
      );

      const result = await response.json();
      setEmailResult(result);
    } catch (error: any) {
      setEmailResult({
        success: false,
        error: error.message,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const testFormEmail = async () => {
    setIsTestingForm(true);
    setFormTestResult('');

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/test-form-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          name: 'Test User',
          email: 'test@example.com',
          message: 'This is a test form submission'
        })
      });

      const result = await response.json();
      setFormTestResult(JSON.stringify(result, null, 2));
    } catch (error) {
      setFormTestResult(JSON.stringify({ error: error.message }, null, 2));
    } finally {
      setIsTestingForm(false);
    }
  };

  const [isTestingForm, setIsTestingForm] = useState(false);
  const [formTestResult, setFormTestResult] = useState('');

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              QBOVerifier Multi-Port Email System Test
            </h1>
            <p className="text-gray-600">
              Test multiple SMTP configurations automatically using Nodemailer
            </p>
          </div>

          {/* SMTP Configuration Display */}
          <div className="bg-blue-50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-blue-900 mb-4">
              Multi-Port Nodemailer Configuration
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-800">Server:</span>
                <span className="ml-2 text-blue-700">mail.bio-mail.com</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Ports Tested:</span>
                <span className="ml-2 text-blue-700">587 (STARTTLS), 465 (SSL), 25 (Plain), 2525 (Alt)</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Username:</span>
                <span className="ml-2 text-blue-700">busdev@bio-mail.com</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Library:</span>
                <span className="ml-2 text-blue-700">Nodemailer 6.9.7</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Function:</span>
                <span className="ml-2 text-blue-700">send-email-nodemailer-multi-port</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Strategy:</span>
                <span className="ml-2 text-blue-700">Auto-detect working port</span>
              </div>
            </div>
          </div>

          {/* Email Input */}
          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Test Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address to receive test email"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          {/* Test Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={testConnection}
              disabled={isTestingConnection}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {isTestingConnection ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Testing...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <i className="ri-wifi-line mr-2"></i>
                  Test Connection
                </span>
              )}
            </button>

            <button
              onClick={sendTestEmail}
              disabled={isSendingEmail}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {isSendingEmail ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Sending Email...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <i className="ri-mail-send-line mr-2"></i>
                  Send Test Email
                </span>
              )}
            </button>
          </div>

          {/* Connection Status Display */}
          {connectionResult && (
            <div className={`rounded-lg p-6 mb-4 ${
              connectionResult.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start mb-2">
                {connectionResult.success ? (
                  <i className="ri-check-circle-fill text-green-600 text-2xl mr-3"></i>
                ) : (
                  <i className="ri-error-warning-fill text-red-600 text-2xl mr-3"></i>
                )}
                <h3 className={`text-xl font-semibold ${
                  connectionResult.success ? 'text-green-900' : 'text-red-900'
                }`}>
                  Connection Test Result
                </h3>
              </div>
              <pre className="bg-white rounded p-4 text-sm overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(connectionResult, null, 2)}
              </pre>
            </div>
          )}

          {/* Email Status Display */}
          {emailResult && (
            <div className={`rounded-lg p-6 ${
              emailResult.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start mb-2">
                {emailResult.success ? (
                  <i className="ri-check-circle-fill text-green-600 text-2xl mr-3"></i>
                ) : (
                  <i className="ri-error-warning-fill text-red-600 text-2xl mr-3"></i>
                )}
                <h3 className={`text-xl font-semibold ${
                  emailResult.success ? 'text-green-900' : 'text-red-900'
                }`}>
                  Email Test Result
                </h3>
              </div>
              <pre className="bg-white rounded p-4 text-sm overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(emailResult, null, 2)}
              </pre>
            </div>
          )}

          {/* Test Form Email */}
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Test Form Email</h3>
            <p className="text-gray-600 mb-6">
              Test the exact same email configuration but through a form submission function.
            </p>
            
            <button
              onClick={testFormEmail}
              disabled={isTestingForm}
              className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap mb-4"
            >
              {isTestingForm ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Testing Form Email...
                </>
              ) : (
                <>
                  <i className="ri-mail-send-line mr-2"></i>
                  Test Form Email
                </>
              )}
            </button>
            
            {formTestResult && (
              <div className="mt-4">
                <h4 className="font-semibold text-gray-900 mb-2">Form Email Test Result</h4>
                <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-auto max-h-96">
                  {formTestResult}
                </pre>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-8 bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              How to Use These Tests
            </h3>
            <div className="space-y-3 text-gray-700 text-sm">
              <div>
                <strong className="text-green-600">Test Connection:</strong> Uses nodemailer.verify() to test SMTP connection
              </div>
              <div>
                <strong className="text-blue-600">Send Test Email:</strong> Uses nodemailer.sendMail() to actually send an email
              </div>
            </div>
            
            <h4 className="text-md font-semibold text-gray-900 mt-4 mb-2">
              Current Configuration
            </h4>
            <div className="text-gray-700 space-y-1 text-sm">
              <div>✅ Using <strong>Nodemailer 6.9.7</strong> library</div>
              <div>✅ Port 25 with no SSL (as you requested)</div>
              <div>✅ Your bio-mail.com credentials</div>
              <div>✅ Enhanced error handling and timeouts</div>
              <div>✅ Industry standard email library</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
